import React, { useEffect, useState, useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import Role from "../../../../utils/roles";
import DatePicker from "../../../../components/DatePicker/DatePicker";

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");

const PolicyApprovalSuggestionCard = ({
  loading,
  approvalDates,     // string[] e.g. ["2026-02-02"]
  possibleApprovers, // [{ name, email, confidenceScore, employee?: { employee_id, ... } }]
  staffList,         // Staff list from parent
  onConfirm,         // (date: string, approverEmployeeId: string) => void (optional)
}) => {
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedApproverEmail, setSelectedApproverEmail] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const employees = staffList?.getEmployees?.data || [];
  const allAdmins = useMemo(() => {
    const admins = employees
      .filter(emp => 
        emp.in_scope !== false && 
        !emp.termination_date &&
        emp.roles?.some(role => role?.role_description?.toLowerCase() === Role.ADMIN.toLowerCase())
      )
      .map(emp => ({
        email: emp.emails?.find((em) => em.type === "work")?.email || emp.emails?.[0]?.email || "",
        name: emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
        employee: { employee_id: emp.employee_id },
      }))
      .filter(a => a.email);
    const adminEmailSet = new Set(admins.map(a => a.email.toLowerCase()));
    const filtered = possibleApprovers?.filter(a => a.email && adminEmailSet.has(a.email.toLowerCase())) || [];
    return { admins, filtered };
  }, [employees, possibleApprovers]);

  const availableApprovers = allAdmins.filtered.length > 0 ? allAdmins.filtered : (approvalDates?.length > 0 ? allAdmins.admins : []);

  useEffect(() => {
    if (approvalDates?.length > 0) setSelectedDate(approvalDates[0]);
  }, [approvalDates]);

  useEffect(() => {
    if (availableApprovers?.length > 0 && !selectedApproverEmail) {
      const best = [...availableApprovers].sort((a, b) => (b.confidenceScore ?? 0) - (a.confidenceScore ?? 0))[0];
      setSelectedApproverEmail(best.email ?? "");
    }
  }, [availableApprovers]);

  const hasData = approvalDates?.length > 0 || availableApprovers?.length > 0;

  const handleConfirmClick = () => setConfirmDialogOpen(true);

  const handleDialogConfirm = async () => {
    setConfirmLoading(true);
    try {
      const approverEmployeeId = selectedApprover?.employee?.employee_id ?? selectedApprover?.employee_id ?? null;
      await onConfirm?.(selectedDate, approverEmployeeId);
      setConfirmed(true);
      setConfirmDialogOpen(false);
    } finally {
      setConfirmLoading(false);
    }
  };

  const selectedApprover = availableApprovers?.find((a) => a.email === selectedApproverEmail);

  const fieldLabelSx = {
    fontSize: "13px",
    fontWeight: 600,
    color: theme.customColors.text.secondary,
    mb: 0.75,
    letterSpacing: "0.3px",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "14px",
      backgroundColor: theme.palette.background.paper,
      "& fieldset": { borderColor: theme.customColors.border.main },
      "&:hover fieldset": { borderColor: theme.customColors.border.strong },
      "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
    },
  };

  if (loading) {
    return (
      <Box
        sx={{
          mt: 2,
          p: "14px 16px",
          borderRadius: "12px",
          border: `1px solid ${theme.customColors.border.subtle}`,
          backgroundColor: theme.customColors.background.cardHeader,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <CircularProgress size={14} thickness={5} />
        <Typography variant="body2" color={theme.customColors.text.secondary}>
          Scanning document for approval details…
        </Typography>
      </Box>
    );
  }
  if (!hasData) return null;

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mt: 2,
          borderRadius: "12px",
          border: `1px solid ${theme.customColors.border.subtle}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.customColors.shadow.card,
        }}
      >
        <CardHeader
          avatar={
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                backgroundColor: theme.customColors.status.info.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AutoAwesomeIcon sx={{ color: theme.customColors.status.info.text, fontSize: 16 }} />
            </Box>
          }
          title={
            <Typography variant="h6" sx={{ fontSize: "15px", fontWeight: 600, color: theme.customColors.text.strong }}>
              Activate policy using document approval details
            </Typography>
          }
          subheader={
            <Typography variant="body2" color={theme.customColors.text.secondary} sx={{ mt: 0.25, lineHeight: 1.5 }}>
              {approvalDates?.length > 0 && allAdmins.filtered.length > 0
                ? <>We found approval date and approver in the document. If you confirm these details, the policy will be set to <strong>active</strong> immediately and the in-app approval step will be skipped. You can still use &quot;Send for Approval&quot; above to use the normal pending-approval flow instead.</>
                : approvalDates?.length > 0
                ? <>We found approval date in the document. Please select an approver and confirm these details to set the policy to <strong>active</strong> immediately. The in-app approval step will be skipped. You can still use &quot;Send for Approval&quot; above to use the normal pending-approval flow instead.</>
                : allAdmins.filtered.length > 0
                ? <>We found approver in the document. Please select an approval date and confirm these details to set the policy to <strong>active</strong> immediately. The in-app approval step will be skipped. You can still use &quot;Send for Approval&quot; above to use the normal pending-approval flow instead.</>
                : ""}
            </Typography>
          }
          sx={{
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            backgroundColor: theme.customColors.background.cardHeader,
            alignItems: "flex-start",
            padding: "14px 16px",
            ".MuiCardHeader-avatar": { mt: 0.25, mr: 1.5 },
          }}
        />

        <CardContent sx={{ p: "20px 16px", display: "flex", flexDirection: "column", gap: 2.5 }}>

          {/* Date + Approver row */}
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "flex-start" }}>

            {/* Date */}
            {(approvalDates?.length > 0 || availableApprovers?.length > 0) && (
              <Box>
                <Typography sx={fieldLabelSx}>Approval date</Typography>
                <DatePicker
                  size="small"
                  value={selectedDate}
                  onChange={(date) => { setSelectedDate(date); setConfirmed(false); }}
                  disabled={confirmed}
                  InputLabelProps={{ shrink: true }}
                  sx={{ ...inputSx, minWidth: 180 }}
                />
                <Typography variant="caption" color={theme.customColors.text.disabled} sx={{ display: "block", mt: 0.5 }}>
                  {approvalDates?.length > 0 
                    ? "Extracted from document. You can change this."
                    : "Select approval date."}
                </Typography>
              </Box>
            )}

            {/* Approver */}
            {availableApprovers?.length > 0 && (
              <Box>
                <Typography sx={fieldLabelSx}>Approver</Typography>
                <TextField
                  select
                  size="small"
                  value={selectedApproverEmail}
                  onChange={(e) => { setSelectedApproverEmail(e.target.value); setConfirmed(false); }}
                  disabled={confirmed}
                  sx={{ ...inputSx, minWidth: 300 }}
                  SelectProps={{
                    renderValue: (email) => {
                      const a = availableApprovers.find((x) => x.email === email);
                      if (!a) return email;
                      return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar sx={{ width: 22, height: 22, fontSize: "9px", bgcolor: "#A9B5FF" }}>
                            {getInitials(a.name)}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: theme.customColors.text.strong, lineHeight: 1.2 }}>
                              {a.name}
                            </Typography>
                            <Typography sx={{ fontSize: "11px", color: theme.customColors.text.secondary, lineHeight: 1.2 }}>
                              {a.email}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    },
                  }}
                >
                  {availableApprovers.map((approver) => (
                    <MenuItem key={approver.email} value={approver.email} sx={{ py: 1.25 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: "10px", bgcolor: "#A9B5FF", flexShrink: 0 }}>
                          {getInitials(approver.name)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: theme.customColors.text.strong }}>
                            {approver.name}
                          </Typography>
                          <Typography variant="caption" color={theme.customColors.text.secondary}>
                            {approver.email}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="caption" color={theme.customColors.text.disabled} sx={{ display: "block", mt: 0.5 }}>
                  {allAdmins.filtered.length > 0 ? "Admin users matched from document." : "Select an approver."}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Divider + action row */}
          <Box
            sx={{
              borderTop: `1px solid ${theme.customColors.border.main}`,
              pt: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {confirmed ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <CheckCircleOutlineIcon sx={{ color: theme.customColors.status.success.text, fontSize: 17 }} />
                  <Typography variant="body2" sx={{ color: theme.customColors.status.success.text, fontWeight: 600 }}>
                    Approval details confirmed
                  </Typography>
                </Box>
                {selectedApprover && (
                  <Tooltip title={selectedApprover.email} arrow>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: "8px", bgcolor: "#A9B5FF" }}>
                        {getInitials(selectedApprover.name)}
                      </Avatar>
                      <Typography variant="caption" color={theme.customColors.text.secondary}>
                        {selectedApprover.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
                {selectedDate && (
                  <Typography variant="caption" color={theme.customColors.text.secondary}>
                    · {selectedDate}
                  </Typography>
                )}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setConfirmed(false)}
                  sx={{
                    ml: "auto",
                    fontSize: "13px",
                    textTransform: "none",
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    p: 0,
                    minWidth: "unset",
                  }}
                >
                  Edit
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                size="small"
                onClick={handleConfirmClick}
                disabled={!selectedDate && !selectedApproverEmail}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  fontSize: "13px",
                  px: 2.5,
                  py: 0.75,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.customColors.button.text,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: theme.customColors.button.hover,
                    boxShadow: "none",
                  },
                  "&:disabled": {
                    backgroundColor: theme.customColors.background.tableRow,
                    color: theme.customColors.text.disabled,
                  },
                }}
              >
                Confirm and activate policy
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => !confirmLoading && setConfirmDialogOpen(false)}
        onConfirm={handleDialogConfirm}
        heading="Activate this policy?"
        content={`This policy will be activated with the approval date and approver you selected.`}
        buttonText="Activate policy"
        loading={confirmLoading}
        note="To use the usual review flow instead, use Send for Approval above."
      />
    </>
  );
};

export default PolicyApprovalSuggestionCard;
